import React, { useEffect, useState, Suspense } from 'react';
import { Loader } from '@mantine/core';
import { Icon, ICONS } from '@/common/components/Icon';
import { initializeCategories } from '@/stores/categoryStore';
import {
    useGetDeletedTodoTitle,
    useTodo,
} from '@/features/todo/services/useTodoQueries';

// 핵심 레이아웃 컴포넌트
import LayoutCore from './LayoutCore';
import Header from './Header';
import Sidebar from './Sidebar';
import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import { TodoDetailDrawer } from '@/features/todo/components/todoDrawer/TodoDetailDrawer';
import { TodoNotFoundModal } from '@/features/todo/components/todoDrawer/TodoNotFoundModal';
import { TodoConfirmModal } from '@/features/todo/components/TodoConfirmModal';
import { useTodoModalStore } from '@/stores/todoModalStore';
import { useMediaQuery } from '@mantine/hooks';
import { MOBILE_WIDTH } from '@/common/constants/constants';
import CommentDeleteModal from '@/features/comment/components/CommentDeleteModal';
import { useCommentDeleteModalStore } from '@/stores/commentDeleteModalStore';
import { useTimezoneSync } from '@/common/hooks/useTimezoneSync';

interface MainLayoutProps {
    children: React.ReactNode;
    contentProps?: {
        noShadow?: boolean;
        noRadius?: boolean;
        style?: React.CSSProperties;
    };
}

const MainLayout = ({ children, contentProps = {} }: MainLayoutProps) => {
    const { noShadow, noRadius, style } = contentProps;
    const isMobile = useMediaQuery(`(max-width: ${MOBILE_WIDTH}px)`);

    // TodoDetailDrawer 상태 관리
    const {
        selectedTodoId,
        isDrawerOpen,
        closeDrawer,
        isNotFoundModalOpen,
        openNotFoundModal,
        closeNotFoundModal,
    } = useTodoDrawerStore();

    const {
        isModalOpen: todoModalOpened,
        closeModal: closeTodoConfirmModal,
        canRemovePlanner: canCancelTodo,
    } = useTodoModalStore();

    const commentDeleteModalStore = useCommentDeleteModalStore();

    // Todo 데이터 조회
    const { data: todo, isLoading } = useTodo(selectedTodoId);
    const { data: todoTitle, isLoading: isLoadingTitle } =
        useGetDeletedTodoTitle(selectedTodoId);

    // 카테고리 초기화
    initializeCategories().catch((error: Error) => {
        console.error('Failed to initialize categories:', error.message);
    });

    useEffect(() => {
        if (!isLoading && !todo && todoTitle && !isLoadingTitle) {
            openNotFoundModal();
        }
    }, [
        todo,
        isLoading,
        todoTitle,
        isLoadingTitle,
        selectedTodoId,
        openNotFoundModal,
    ]);


    // React.memo 사용 컴포넌트 - 성능 향상을 위해 재렌더링 최소화
    const MemoizedHeader = React.memo(Header);
    const MemoizedSidebar = React.memo(Sidebar);

    // 시간대 동기화 훅 실행
    useTimezoneSync();

    return (
        <LayoutCore
            header={<MemoizedHeader />}
            navbar={<MemoizedSidebar />}
            contentProps={{ noShadow, noRadius, style }}
        >
            {/* Suspense로 감싸 모든 지연 로딩 컴포넌트 처리 */}
            <Suspense
                fallback={
                    <Loader
                        w="100%"
                        h="100%"
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    />
                }
            >
                {/* Todo가 유효한 경우에만 드로어 지연 로딩 */}
                {!isLoading && todo && (
                    <TodoDetailDrawer
                        opened={isDrawerOpen}
                        onClose={closeDrawer}
                        todo={todo}
                    />
                )}

                {/* Todo를 찾을 수 없는 경우 모달 */}
                {isNotFoundModalOpen && (
                    <TodoNotFoundModal
                        opened={isNotFoundModalOpen}
                        onClose={closeNotFoundModal}
                        todoTitle={todoTitle}
                    />
                )}

                {/* 삭제 모달 */}
                {todoModalOpened && (
                    <TodoConfirmModal
                        opened={todoModalOpened}
                        onClose={closeTodoConfirmModal}
                        canCancelTodo={canCancelTodo}
                    />
                )}

                {/* 댓글 삭제 모달 */}
                {commentDeleteModalStore.isOpen && (
                    <CommentDeleteModal
                        opened={commentDeleteModalStore.isOpen}
                        onClose={commentDeleteModalStore.closeModal}
                        onConfirm={commentDeleteModalStore.onConfirm}
                        targetId={commentDeleteModalStore.targetId}
                        hasReply={commentDeleteModalStore.hasReply}
                    />
                )}

                {/* 메인 콘텐츠 */}
                {children}
            </Suspense>
        </LayoutCore>
    );
};

// React.memo를 사용하여 불필요한 리렌더링 방지
export default React.memo(MainLayout);
